import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormArray, FormBuilder, FormGroup, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UiKitModule } from '@serviceplan/ui-kit';
import { AssetUIModel, ClientCoreModule, CommonUtilityLib, getRelationTypeName, RelationModel } from '@thehubz/client/core';
import { RelationsApiService, RelationshipType } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { map, Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'thehubz-asset-relations-form-control',
  templateUrl: './asset-relations-form-control.component.html',
  styleUrls: ['./asset-relations-form-control.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AssetRelationsFormControlComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatIconModule, UiKitModule, ClientCoreModule],
})
export class AssetRelationsFormControlComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() assets: AssetUIModel[] = [];
  @Input() assetsRelation: RelationModel = { assets: [], assetDetails: [] };
  @Input() invalidFormState!: Observable<boolean>;
  relationTypes: RelationshipType[] = [];
  value!: { assets: AssetRelationsUIModel[] };
  formStateChange = false;

  onChange!: (value: AssetRelationsUIModel[]) => void;
  onTouched!: () => void;

  selectRelationsForm!: FormGroup;

  formArray!: FormArray;

  destroy$ = new Subject<void>();

  textIsRequired = $localize`Relations type is required`;

  constructor(
    private fb: FormBuilder,
    private readonly relationsService: RelationsApiService,
    private commonUtilityLib: CommonUtilityLib,
  ) {}

  ngOnInit() {
    this.updateAssetsRelationData();
    this.selectRelationsForm = this.fb.group({
      assets: this.fb.array(
        this.assets.map((asset) =>
          this.fb.group({
            assetId: [asset._id],
            relationTypes: ['', [Validators.required]],
          }),
        ),
      ),
    });
    this.formArray = this.selectRelationsForm.get('assets') as FormArray;

    this.getRelationTypes();
    this.updateValue();
    this.invalidFormState.subscribe((invalid) => {
      if (invalid) {
        this.formStateChange = invalid;
      }
    });
  }

  updateAssetsRelationData() {
    this.assetsRelation.assetDetails = this.assets;
    this.assetsRelation.assets = this.assets.map((asset) => {
      const existingRelation = this.assetsRelation.assets.find((relation) => relation.assetId === asset._id);
      return {
        assetId: asset._id,
        relationTypes: existingRelation ? existingRelation.relationTypes : [],
      };
    });
  }

  /**
   * Method to update the value of ValueAccessor when the form value changes.
   */
  private updateValue() {
    this.selectRelationsForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!this.onChange) {
        return;
      }
      this.onChange(value);
      this.onTouched();
    });
  }

  deleteAsset(index: number) {
    const control = this.selectRelationsForm.get('assets') as FormArray;
    this.assets.splice(index, 1);
    control.removeAt(index);
  }

  writeValue(value: { assets: AssetRelationsUIModel[] }): void {
    if (!value.assets || value.assets.length === 0) {
      if (this.assetsRelation.assets.length > 0) {
        value = this.assetsRelation;
      }
    }
    if (value) {
      this.selectRelationsForm.patchValue({
        assets: this.mapAssetRelationsToForm(value),
      });
    }
  }

  private mapAssetRelationsToForm(assetRelations: { assets: AssetRelationsUIModel[] }) {
    if (!assetRelations || !assetRelations.assets) {
      return [];
    }

    return assetRelations.assets.map((assetRelation) => ({
      assetId: assetRelation.assetId,
      relationTypes: assetRelation.relationTypes,
    }));
  }

  registerOnChange(fn: (value: AssetRelationsUIModel[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  private getRelationTypes() {
    this.relationsService
      .relationsControllerFindAllRelationTypes()
      .pipe(
        map((relationTypes) =>
          relationTypes.map((relationType) => ({
            ...relationType,
            name: getRelationTypeName(relationType.name),
          })),
        ),
      )
      .subscribe((relationTypes) => {
        this.relationTypes = relationTypes;
      });
  }

  /**
   * Remove chip from multi select
   * @param value
   */
  removeChip(event: { formControlName: string; list: AnyType }, formArrayIndex: number) {
    const formControlValue = this.formArray.controls[formArrayIndex].get(event.formControlName)?.value;
    const modifiedFormControlValue = this.commonUtilityLib.removeObjectFromArray(formControlValue, event.list._id);
    this.formArray.controls[formArrayIndex].get(event.formControlName)?.patchValue(modifiedFormControlValue);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

export interface AssetRelationsUIModel {
  assetId: string;
  relationTypes: RelationshipType[];
}
